import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { stayExtensionText, StayExtensionText } from '../i18n/pages/stayExtension';
import {
  StayExtensionBlock,
  StayExtensionDeclineReason,
  StayExtensionGuestRequest,
  StayExtensionGuideSection,
  StayExtensionType,
  getStayExtensionSection,
  isArrival,
  requestStayExtension,
  remindStayExtensionHost,
  withdrawStayExtension,
} from '../api.stayExtension';
import { stayExtensionChipLabel } from '../utils/stayExtensionChipLabel';
import clockIcon from '../assets/change_time.png';
import './StayExtensionGuestSection.css';

interface Props {
  formToken: string;
}

/** Chronological through the stay — bags can arrive before the guest and outlast them. */
const ORDER: Record<StayExtensionType, number> = {
  LUGGAGE_DROPOFF: 0,
  EARLY_CHECKIN: 1,
  LATE_CHECKOUT: 2,
  LUGGAGE_PICKUP: 3,
};

/** The short name for one product at one end: on chips, on strips, on action buttons. See
 * {@link stayExtensionChipLabel} for why this used to be its own distinct-per-type bug. */
const label = stayExtensionChipLabel;

/** The heading above one block's list of hours. */
const blockTitle = (type: StayExtensionType, t: StayExtensionText) => {
  switch (type) {
    case 'EARLY_CHECKIN': return t.earlyTitle;
    case 'LATE_CHECKOUT': return t.lateTitle;
    case 'LUGGAGE_DROPOFF': return t.luggageDropoffTitle;
    case 'LUGGAGE_PICKUP': return t.luggagePickupTitle;
  }
};

/**
 * The guest's half: what their times are, what is in flight, and what they could still ask for.
 *
 * <p>Collapsed is the only resting state. An earlier version sprang open whenever something was in
 * flight, which meant the chevron did nothing at all for anyone who had asked for something — so
 * the shut row now has to answer the question by itself, and does: a question as the heading, the
 * guest's actual times labelled underneath it, then requests, then offers.
 *
 * <p>What is never shown is <em>why</em> an hour needs asking. That reason always concerns the
 * neighbouring stay, and describing it would mean telling this guest about another guest's booking.
 * "Ask host" is the whole message. The one exception is a decline reason the manager deliberately
 * picked from a fixed set.
 */
const StayExtensionGuestSection: React.FC<Props> = ({ formToken }) => {
  const { lang } = useLang();
  const t = stayExtensionText[lang];

  const [section, setSection] = useState<StayExtensionGuideSection | null>(null);
  const [note, setNote] = useState('');
  const [busyTime, setBusyTime] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [reminding, setReminding] = useState<number | null>(null);
  const [reminded, setReminded] = useState(false);
  // Which request is asking "are you sure". Withdrawing is the one irreversible thing on this
  // screen — the hour goes back on sale and the guest re-joins the queue behind whoever asked
  // since — so it takes two taps. In place rather than window.confirm(), which renders in the
  // browser's language and would be the one untranslated string in the section.
  const [confirming, setConfirming] = useState<number | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      setSection(await getStayExtensionSection(formToken));
    } catch {
      // A guide that cannot load this block is still a perfectly good guide: stay silent and render
      // nothing rather than putting an error where an optional extra would have been.
      setSection(null);
    }
  }, [formToken]);

  useEffect(() => { void load(); }, [load]);

  const submit = async (type: StayExtensionType, time: string) => {
    setBusyTime(time);
    setError(null);
    try {
      await requestStayExtension(formToken, { type, time, guestNote: note.trim() || undefined });
      setNote('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || t.requestError);
    } finally {
      setBusyTime(null);
    }
  };

  const cancel = async (requestId: number) => {
    setError(null);
    setConfirming(null);
    try {
      await withdrawStayExtension(formToken, requestId);
      await load();
    } catch {
      setError(t.actionError);
    }
  };

  const remind = async (requestId: number) => {
    setReminding(requestId);
    setError(null);
    try {
      await remindStayExtensionHost(formToken, requestId);
      setReminded(true);
      // Reload so the button picks up the new cooling-off window from the server rather than
      // guessing at it locally — the server is the one keeping the clock.
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || t.actionError);
    } finally {
      setReminding(null);
    }
  };

  /** Opens the section and brings the asked-for half into view. */
  const openAt = (type: StayExtensionType) => {
    setOpen(true);
    window.setTimeout(() => {
      bodyRef.current
        ?.querySelector(`[data-block="${type}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  };

  if (!section?.enabled) return null;

  // Chronological through the stay: bags can arrive before the guest does, and outlast them.
  const blocks = [
    section.luggageDropoff, section.earlyCheckin, section.lateCheckout, section.luggagePickup,
  ].filter(Boolean) as StayExtensionBlock[];
  // Anything settled in the guest's favour is already reflected in their times, and anything
  // withdrawn or expired is not worth a line. What remains is what they can act on or should know.
  const live = section.requests.filter(
    r => r.status === 'REQUESTED' || r.status === 'APPROVED' || r.status === 'DECLINED');
  const askable = blocks.filter(b => b.options.length > 0);
  if (blocks.length === 0 && live.length === 0) return null;

  const money = (v: number | null) =>
    v === null || v <= 0 ? t.free : `${v.toLocaleString(lang === 'en' ? 'en-GB' : lang)} ${section.currency}`;
  const fill = (s: string, v: Record<string, string>) =>
    Object.entries(v).reduce((a, [k, x]) => a.replace(`{${k}}`, x), s);

  // The question narrows to whatever is priced: asking about both when only one is on offer
  // invites the guest to hunt for something that is not there. An apartment selling storage but no
  // hours asks about bags instead — the times are not on the table there at all.
  const hasEarly = !!section.earlyCheckin;
  const hasLate = !!section.lateCheckout;
  const heading = !hasEarly && !hasLate ? t.askHeaderLuggage
    : hasEarly && hasLate ? t.askHeaderBoth
    : hasEarly ? t.askHeaderEarly : t.askHeaderLate;

  // The luggage blocks carry the same standard hours, so the shut row still shows the guest's real
  // times at an apartment that only sells storage. Only the hours products can move them.
  const inTime = section.earlyCheckin?.grantedTime
    || section.earlyCheckin?.standardTime || section.luggageDropoff?.standardTime;
  const outTime = section.lateCheckout?.grantedTime
    || section.lateCheckout?.standardTime || section.luggagePickup?.standardTime;
  const anyGranted = !!(section.earlyCheckin?.grantedTime || section.lateCheckout?.grantedTime);

  // Where the bags go, once there are bags to put somewhere. Falls back to the manager's own
  // language when they wrote no translation — the same rule the rest of the guide follows.
  const bagsWhere = lang === 'cs'
    ? section.luggageInstructions
    : section.luggageInstructionsEn || section.luggageInstructions;

  // Chronological: check-in before check-out, because that is the order the guest will live them.
  // Status decides nothing here — the actions moved below the strips, so a strip is now pure
  // information and there is no "most actionable" one to hoist. Status only breaks a tie between
  // two requests at the same end, which the one-open-request-per-end rule makes rare.
  const rank: Record<string, number> = { APPROVED: 0, REQUESTED: 1, DECLINED: 2 };
  const sorted = [...live].sort((a, b) =>
    (a.type === b.type ? rank[a.status] - rank[b.status] : ORDER[a.type] - ORDER[b.type]));

  const payable = sorted.filter(r => r.status === 'APPROVED' && r.paymentUrl);
  // An approved offer is cancellable right up until it is paid for. The guest may have realised
  // they need different hours, or not liked the time and price the host came back with, or simply
  // changed their mind — and until money moves, none of that is the host's business to adjudicate.
  // A free approval never appears here: it is granted outright and its status is already PAID.
  const withdrawable = sorted.filter(
    r => r.status === 'REQUESTED' || (r.status === 'APPROVED' && r.paymentUrl));

  return (
    <div className="sx-sec">
      <button type="button" className="sx-row" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="sx-ic" aria-hidden="true">
          <span
            className="m"
            style={{ WebkitMaskImage: `url(${clockIcon})`, maskImage: `url(${clockIcon})` } as React.CSSProperties}
          />
        </span>
        <span className="sx-txt">
          <span className="sx-ttl">{heading}</span>
          <span className="sx-times">
            <span className="lab">{anyGranted ? t.timesYours : t.timesStandard}</span>
            {inTime && <> {t.timesIn} <b>{inTime}</b></>}
            {inTime && outTime && ' · '}
            {outTime && <>{t.timesOut} <b>{outTime}</b></>}
            {anyGranted && ' ✓'}
          </span>
        </span>
        <span className="sx-chev" aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>

      {sorted.map(request => (
        <RequestStrip key={request.id} request={request} t={t} fill={fill} money={money} />
      ))}

      {/* Arrives only once storage has been paid for — the backend sends nothing before that, so
          there is no state where this renders an empty promise. Above the offer chips because a
          guest who has already bought is looking for this, not for more to buy. */}
      {bagsWhere && (
        <div className="sx-bags">
          <span className="sx-bags-ttl">🧳 {t.luggageWhereTitle}</span>
          <span className="sx-bags-body">{bagsWhere}</span>
        </div>
      )}

      {/* Actions come after every strip rather than each sitting under its own. With an approved
          request and a waiting one, a pay button in between split the two apart and read as
          belonging to whichever strip you happened to look at. */}
      {payable.map(request => (
        <a key={request.id} className="sx-pay" href={request.paymentUrl!}>
          {t.payNow}
          {payable.length > 1 && ` · ${describe(request, t)}`}
          {' · '}{money(request.price)}
        </a>
      ))}

      {withdrawable.map(request => {
        // Server-enforced: the button being disabled is a courtesy, the real limit is a stored
        // timestamp. This endpoint is public and sends mail, so a UI-only throttle would be none.
        const waiting = request.status === 'REQUESTED';
        const nextAt = request.canRemindAt ? new Date(request.canRemindAt) : null;
        const canRemind = !nextAt || nextAt.getTime() <= Date.now();
        // Once the host has answered, the hour under discussion is the one they agreed to, which
        // is not always the one that was asked for.
        const time = request.approvedTime || request.requestedTime;

        // The prompt replaces the action row rather than sitting under it, so the two buttons
        // being decided between are the only two on screen.
        if (confirming === request.id) {
          return (
            <div key={request.id} className="sx-confirm">
              <div className="sx-confirm-q">
                {fill(t.withdrawConfirmQ, { time })}
              </div>
              {/* Giving up an offer the host already agreed to costs more than giving up a
                  question they have not answered yet, and the warning says which one this is. */}
              <div className="sx-confirm-why">
                {waiting ? t.withdrawConfirmWhy : t.withdrawConfirmWhyApproved}
              </div>
              <div className="sx-confirm-row">
                <button type="button" className="sx-keep" onClick={() => setConfirming(null)}>
                  {t.withdrawKeep}
                </button>
                <button type="button" className="sx-yes" onClick={() => cancel(request.id)}>
                  {t.withdrawYes}
                </button>
              </div>
            </div>
          );
        }

        // Chasing only makes sense while nobody has answered. Once approved, the pay button above
        // is the thing to do and cancelling stands alone underneath it.
        return (
          <div key={request.id} className={`sx-waiting-acts${waiting ? '' : ' solo'}`}>
            {waiting && (
              <button
                type="button" className="sx-remind"
                disabled={!canRemind || reminding === request.id}
                onClick={() => remind(request.id)}
              >
                {canRemind
                  ? t.remindCta
                  : fill(t.remindWait, { time: nextAt!.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}
                {withdrawable.length > 1 && canRemind && ` · ${describe(request, t)}`}
              </button>
            )}
            <button type="button" className="sx-withdraw" onClick={() => setConfirming(request.id)}>
              {t.withdraw}
              {!waiting && withdrawable.length > 1 && ` · ${describe(request, t)}`}
            </button>
          </div>
        );
      })}

      {askable.length > 0 && (
        <>
          <div className="sx-chips-head">
            {live.some(r => r.status === 'DECLINED') ? t.chipsCanStillAsk : t.chipsStillAvailable}
          </div>
          <div className="sx-chips">
            {askable.map(block => {
              const early = isArrival(block.type);
              // Options come closest-to-standard first, so the last is the outermost hour on offer
              // and the cheapest is the first — exactly the two numbers a chip wants.
              const limit = block.options[block.options.length - 1];
              const cheapest = Math.min(...block.options.map(o => o.price));
              return (
                <button
                  key={block.type} type="button" className="sx-chip"
                  onClick={() => openAt(block.type)}
                >
                  <span className="lb">
                    {early && <span className="ar" aria-hidden="true">←</span>}
                    {label(block.type, t)}
                    {!early && <span className="ar" aria-hidden="true">→</span>}
                  </span>
                  <span className="tm">
                    {fill(early ? t.chipFrom : t.chipTill, { time: limit.time })}
                  </span>
                  <span className="st">{money(cheapest)}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {reminded && !error && <div className="sx-sent">{t.remindSent}</div>}
      {error && <div className="sx-error">{error}</div>}

      {open && askable.length > 0 && (
        <div className="sx-exp" ref={bodyRef}>
          {askable.map(block => {
            return (
              <React.Fragment key={block.type}>
                <div className="sx-sub" data-block={block.type}>
                  {blockTitle(block.type, t)}
                </div>
                {block.options.map(option => (
                  <button
                    key={option.time} type="button" className="sx-opt"
                    disabled={busyTime !== null}
                    onClick={() => submit(block.type, option.time)}
                  >
                    <span className="t">{option.time}</span>
                    <span />
                    <span className={`sx-badge ${option.instant ? 'now' : 'ask'}`}>
                      {option.instant ? t.instantBadge : t.askBadge}
                    </span>
                    <span className="p">
                      {busyTime === option.time ? t.submitting : money(option.price)}
                    </span>
                  </button>
                ))}
              </React.Fragment>
            );
          })}

          <input
            type="text" value={note} maxLength={255}
            onChange={e => setNote(e.target.value)}
            placeholder={t.notePlaceholder}
            aria-label={t.noteLabel}
            className="sx-note"
          />
        </div>
      )}
    </div>
  );
};

/** "Check-in 12:00" — used on the strip, and on an action button when there is more than one. */
const describe = (request: StayExtensionGuestRequest, t: StayExtensionText) =>
  `${label(request.type, t)} ${request.approvedTime || request.requestedTime}`;

/**
 * One request in flight, drawn as an arrow pointing the way its hours move. The direction is the
 * shape rather than a word, which is worth about a line of text per strip on a card this size.
 *
 * <p>Information only: the pay and withdraw buttons live below every strip rather than under their
 * own, so nothing splits one strip from the next.
 */
const RequestStrip: React.FC<{
  request: StayExtensionGuestRequest;
  t: StayExtensionText;
  fill: (s: string, v: Record<string, string>) => string;
  money: (v: number | null) => string;
}> = ({ request, t, fill, money }) => {
  const early = isArrival(request.type);
  const approved = request.status === 'APPROVED';

  const tone = approved ? 'approved' : request.status === 'DECLINED' ? 'declined' : 'waiting';
  const head = approved ? t.reqApproved
    : request.status === 'DECLINED' ? t.reqNotPossible : t.reqYours;

  const reasons: Record<StayExtensionDeclineReason, string> = {
    CLEANING: t.dCleaning,
    OTHER_BOOKING: t.dOtherBooking,
    SHORT_NOTICE: t.dShortNotice,
    NO_SPACE: t.dNoSpace,
    UNSPECIFIED: t.dUnspecified,
  };

  return (
    <div className={`sx-req ${tone} ${early ? 'left' : 'right'}`}>
      <span className="rhead">
        {/* A host saying yes is the good news on this card, and the tick finds it before the
            words do. Deliberately green against the blue fill rather than tinted into it. */}
        {approved && <span className="tick" aria-hidden="true">✓</span>}
        {head}
      </span>
      <span className="rmain">
        <span className="rwhat">{describe(request, t)}</span>
        {request.status === 'REQUESTED' && <span className="rtag">{t.reqWaiting}</span>}
        {approved && <span className="rtag">{money(request.price)}</span>}
      </span>
      {request.status === 'DECLINED' && (
        <span className="rwhy">{reasons[request.declineReason ?? 'UNSPECIFIED']}</span>
      )}
      {approved && request.quoteExpiresAt && (
        <span className="rwhy">
          {fill(t.quoteUntil, { date: new Date(request.quoteExpiresAt).toLocaleDateString() })}
        </span>
      )}
    </div>
  );
};

export default StayExtensionGuestSection;
